import {type FC, memo, useMemo} from 'react';
import styles from './GameComponents.module.css';
import Image from '../../../../../../shared/components/Image';
import { getCampaignGameCardIcon } from '../../../../../campaigns/mocks/campaignMockData';
import { useCampaigns, isPrizePopSource, isRebateSource } from '../../../../../campaigns/store/campaignsStore';
import {
  TOURNAMENTS_CAMPAIGN_TYPE,
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  type IPrizePopCampaign,
  type IRebateCampaign
} from '../../../../../campaigns/types/campaign.types';
import { evaluateCampaignStatus } from '../../../../../campaigns/hooks/data/campaignStatus/useCampaignStatus';

interface CampaignLogoProps {
  shouldShowTournamentCup?: boolean;
  gameKey?: string;
}

/**
 * One-shot membership check: is `gameKey` covered by any non-finished campaign in
 * `campaigns`? Uses the pure `evaluateCampaignStatus` from useCampaignStatus.
 */
const isGameInActiveCampaign = (
  campaigns: (IPrizePopCampaign | IRebateCampaign)[],
  gameKey: string | undefined,
): boolean => {
  if (!gameKey || campaigns.length === 0) return false;
  return campaigns.some(campaign => !evaluateCampaignStatus(campaign).isFinished && campaign.games.some(g => g === gameKey));
};

const CampaignLogo: FC<CampaignLogoProps> = ({shouldShowTournamentCup, gameKey}) => {
  const prizePopCampaigns = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE);
  const rebateCampaigns = useCampaigns(REBATE_CAMPAIGN_TYPE);

  const prizePopSourceCampaigns = useMemo(() => prizePopCampaigns.filter(isPrizePopSource), [prizePopCampaigns]);
  const rebateSourceCampaigns = useMemo(() => rebateCampaigns.filter(isRebateSource), [rebateCampaigns]);

  const isGameInPrizePop = useMemo(
    () => isGameInActiveCampaign(prizePopSourceCampaigns, gameKey),
    [prizePopSourceCampaigns, gameKey]
  );
  const isGameInRebate = useMemo(
    () => isGameInActiveCampaign(rebateSourceCampaigns, gameKey),
    [rebateSourceCampaigns, gameKey]
  );

  if (!isGameInPrizePop && !isGameInRebate && !shouldShowTournamentCup) {
    return null;
  }

  return (
    <div className={`${styles.cupContainer} ${styles.cupAppear}`}>
      {shouldShowTournamentCup && <Image src={getCampaignGameCardIcon(TOURNAMENTS_CAMPAIGN_TYPE)} loading="eager" />}
      {isGameInPrizePop && <Image src={getCampaignGameCardIcon(PRIZE_POP_CAMPAIGN_TYPE)} loading="eager" />}
      {isGameInRebate && <Image src={getCampaignGameCardIcon(REBATE_CAMPAIGN_TYPE)} loading="eager" />}
    </div>
  );
};

export default memo(CampaignLogo);
